import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if user wants persistent session
const isPersistent = () => localStorage.getItem('rememberSession') === 'true';

// Secure token storage utilities
// Uses localStorage if "remember me" is enabled, otherwise sessionStorage
const TokenStorage = {
  getAccessToken: () => {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  },
  getRefreshToken: () => {
    return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
  },
  setTokens: (accessToken, refreshToken, remember = false) => {
    // Clear both storages first
    TokenStorage.clearTokens();
    
    // Store preference
    if (remember) {
      localStorage.setItem('rememberSession', 'true');
    }
    
    const storage = remember || isPersistent() ? localStorage : sessionStorage;
    if (accessToken) storage.setItem('accessToken', accessToken);
    if (refreshToken) storage.setItem('refreshToken', refreshToken);
  },
  clearTokens: () => {
    // Clear from both storages
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('rememberSession');
  },
  isRemembered: () => isPersistent()
};

// Export for use in AuthContext
export { TokenStorage };

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = TokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = TokenStorage.getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, null, {
            params: { refresh_token: refreshToken }
          });
          const { access_token, refresh_token: newRefresh } = response.data;
          TokenStorage.setTokens(access_token, newRefresh);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        TokenStorage.clearTokens();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Reservations API
export const reservationsApi = {
  calculatePrice: (startDt, endDt) =>
    api.get('/reservations/calculate-price', { params: { start_dt: startDt, end_dt: endDt } }),
  
  create: (data) => api.post('/reservations/create', data),
  
  getById: (id) => api.get(`/reservations/${id}`),
  
  getByCode: (code) => api.get(`/reservations/code/${code}`),
  
  getMyReservations: () => api.get('/reservations/user/me'),
  
  // Alias for AccountPage
  getMine: () => api.get('/reservations/user/me'),
};

// Invoices API
export const invoicesApi = {
  generate: (code) => api.post(`/invoices/generate/${code}`),
  
  getByCode: (code) => api.get(`/invoices/reservation/${code}`),
  
  downloadPdf: (code) => `${API_URL}/api/invoices/download/${code}`,
  
  downloadReceipt: (code) => `${API_URL}/api/invoices/receipt/${code}`,
  
  // Billing Customers
  getCustomers: (search) => api.get('/invoices/customers', { params: { search } }),
  
  createCustomer: (data) => api.post('/invoices/customers', data),
  
  updateCustomer: (id, data) => api.put(`/invoices/customers/${id}`, data),
  
  deleteCustomer: (id) => api.delete(`/invoices/customers/${id}`),
};

// Payments API
export const paymentsApi = {
  createCheckoutSession: (reservationId, originUrl) =>
    api.post('/payments/checkout/session', null, {
      params: { reservation_id: reservationId, origin_url: originUrl }
    }),
  
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
  
  confirmCash: (reservationId) => api.post(`/payments/cash/confirm/${reservationId}`),
};

// Worker API
export const workerApi = {
  // Dashboard consolidado con las 3 listas
  getDashboard: () => api.get('/worker/dashboard'),
  
  scanQR: (qrToken) => api.post('/worker/scan', { qr_token: qrToken }),
  
  // Acciones rápidas
  quickCheckIn: (reservationId) =>
    api.post(`/worker/reservations/${reservationId}/quick-checkin`),
  
  quickCheckOut: (reservationId) =>
    api.post(`/worker/reservations/${reservationId}/quick-checkout`),
  
  markPaid: (reservationId) =>
    api.post(`/worker/reservations/${reservationId}/mark-paid`),
  
  // Acciones con datos adicionales
  manualCheckIn: (reservationId, data) =>
    api.post(`/worker/reservations/${reservationId}/check-in`, data),
  
  manualCheckOut: (reservationId, data) =>
    api.post(`/worker/reservations/${reservationId}/check-out`, data),
  
  completeReservation: (reservationId) =>
    api.post(`/worker/reservations/${reservationId}/complete`),
  
  updateNotes: (reservationId, notes) =>
    api.put(`/worker/reservations/${reservationId}/notes`, null, {
      params: { internal_notes: notes }
    }),
  
  updateZone: (reservationId, zone) =>
    api.put(`/worker/reservations/${reservationId}/zone`, null, {
      params: { zone }
    }),
  
  // Legacy endpoints (still available)
  getTodayArrivals: () => api.get('/worker/today/arrivals'),
  getTodayDepartures: () => api.get('/worker/today/departures'),
  getPending: () => api.get('/worker/today/pending'),
};

// Admin API
export const adminApi = {
  getReservations: (params) => api.get('/admin/reservations', { params }),
  
  getReservation: (id) => api.get(`/admin/reservations/${id}`),
  
  updateReservation: (id, updates) => api.put(`/admin/reservations/${id}`, updates),
  
  deleteReservation: (id) => api.delete(`/admin/reservations/${id}`),
  listDeletedReservations: (limit = 100) => api.get(`/admin/deleted-reservations`, { params: { limit } }),
  restoreDeletedReservation: (deletionId) => api.post(`/admin/deleted-reservations/${deletionId}/restore`),
  
  resendReservationEmail: (id) => api.post(`/admin/reservations/${id}/resend`),
  
  getTodayArrivals: () => api.get('/admin/reservations/today/arrivals'),
  
  getTodayDepartures: () => api.get('/admin/reservations/today/departures'),
  
  getReservationsByDate: (date, filterType) => 
    api.get('/admin/reservations/by-date', { params: { date, filter_type: filterType } }),
  
  getPricing: () => api.get('/admin/pricing'),
  
  updatePricing: (config) => api.put('/admin/pricing', config),
  
  addSpecialDate: (date, priceCents, reason) =>
    api.post('/admin/pricing/special-date', null, {
      params: { date, price_cents: priceCents, reason }
    }),
  
  removeSpecialDate: (date) => api.delete(`/admin/pricing/special-date/${date}`),
  
  // New pricing endpoints for advanced mode
  addCustomDate: (date, priceCents, reason) =>
    api.post('/admin/pricing/custom-date', null, {
      params: { date, price_cents: priceCents, reason }
    }),
  
  removeCustomDate: (date) => api.delete(`/admin/pricing/custom-date/${date}`),
  
  addBulkDates: (dates, priceCents, reason) =>
    api.post('/admin/pricing/bulk-dates', { dates, price_cents: priceCents, reason }),
  
  addDateRange: (startDate, endDate, priceCents, reason) =>
    api.post('/admin/pricing/date-range', { 
      start_date: startDate, 
      end_date: endDate, 
      price_cents: priceCents, 
      reason 
    }),
  
  clearCustomDates: () => api.delete('/admin/pricing/clear-custom-dates'),
  
  getPricingCalendar: (year, month) => 
    api.get(`/admin/pricing/calendar/${year}/${month}`),
  
  getBlackouts: () => api.get('/admin/blackouts'),
  
  createBlackout: (startDt, endDt, reason) =>
    api.post('/admin/blackouts', null, {
      params: { start_dt: startDt, end_dt: endDt, reason }
    }),
  
  deleteBlackout: (id) => api.delete(`/admin/blackouts/${id}`),
  
  getUsers: (role) => api.get('/admin/users', { params: { role } }),
  
  createUser: (data) => api.post('/admin/users', data),
  
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  
  getStats: () => api.get('/admin/stats'),
  
  getAnalytics: (days = 30) => api.get('/admin/analytics', { params: { days } }),
  
  getMonthlySummary: (year, month) => 
    api.get('/admin/monthly-summary', { params: { year, month } }),
  
  getReservationsByMonth: () => api.get('/admin/reservations-by-month'),
  
  exportMonthlyReport: (params) =>
    api.get('/admin/reports/monthly-export', { params, responseType: 'blob' }),
};

// Marketing API
export const marketingApi = {
  // Public
  validateDiscount: (code) => api.post('/marketing/validate-discount', { code }),
  getLoyaltyDiscount: (email) => api.get(`/marketing/loyalty-discount/${encodeURIComponent(email)}`),
  
  // Admin - Contacts
  getContacts: (params) => api.get('/marketing/admin/contacts', { params }),
  exportContacts: (params) => api.get('/marketing/admin/contacts/export', { params, responseType: 'blob' }),
  deleteContactGDPR: (email) => api.delete(`/marketing/admin/contacts/${encodeURIComponent(email)}/gdpr`),
  
  // Admin - Campaigns
  getCampaigns: (params) => api.get('/marketing/admin/campaigns', { params }),
  createCampaign: (data) => api.post('/marketing/admin/campaigns', data),
  sendCampaign: (campaignId) => api.post(`/marketing/admin/campaigns/${campaignId}/send`),
  getPreviewCount: (campaignId) => api.get(`/marketing/admin/campaigns/${campaignId}/preview-count`),
  
  // Admin - Discount Codes
  getDiscountCodes: (params) => api.get('/marketing/admin/discount-codes', { params }),
  createDiscountCode: (data) => api.post('/marketing/admin/discount-codes', data),
  updateDiscountCode: (code, data) => api.put(`/marketing/admin/discount-codes/${encodeURIComponent(code)}`, data),
  deleteDiscountCode: (code) => api.delete(`/marketing/admin/discount-codes/${encodeURIComponent(code)}`),
  
  // Admin - Metrics & Logs
  getMetrics: () => api.get('/marketing/admin/metrics'),
  getRevenueSummary: () => api.get('/marketing/admin/revenue-summary'),
  getAutomationLogs: (params) => api.get('/marketing/admin/automation-logs', { params }),
};

export default api;
